import { FC, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  GENERIC_UPLOAD_FORM_FIELDS,
  GenericUploadStepEnum,
  IGenericUploadDrawerProps,
  IGenericUploadForm,
  IInjectedDrawerProps,
  simpleResultErrorInfo,
} from "@ryde/shared";

import { CommonDefaultDrawer } from "../common-default-drawer";
import { BaseDrawer } from "../drawer";
import { GenericUploadFooter } from "./generic-upload-footer";
import { GenericUploadForm } from "./generic-upload-form";
import { GenericUploadPreview } from "./generic-upload-preview";
import { GenericUploadProcess } from "./generic-upload-process";
import { excludeSkippedUploadRows } from "./helpers";
import {
  useGenericUploadEntityCreateFields,
  useGenericUploadPhoneConflicts,
  useGenericUploadPreparingFile,
} from "./hooks";
import { useGenericUploadProcess } from "./hooks/use-generic-upload-process";

export const GenericUploadDrawer: FC<
  IGenericUploadDrawerProps & IInjectedDrawerProps
> = ({ drawerNumber, onCloseDrawer, entity }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<GenericUploadStepEnum>(
    GenericUploadStepEnum.Upload,
  );
  const {
    isProcessing,
    processStatistics,
    processFailedItems,
    processUploadData,
  } = useGenericUploadProcess(entity);
  const { isPreparing, uploadStatistics, uploadResult, prepareFile } =
    useGenericUploadPreparingFile(entity);
  const {
    phoneConflicts,
    skippedDataIndexes,
    isExistingPhoneCheckFailed,
    isCheckingPhones,
    unresolvedPhoneConflictRowsCount,
    checkPhoneConflicts,
    toggleSkippedRow,
  } = useGenericUploadPhoneConflicts(entity);

  const initialEntityCreateFields = useGenericUploadEntityCreateFields(entity);

  const [prepareError, setPrepareError] = useState<string | undefined>(
    undefined,
  );

  const methods = useForm<IGenericUploadForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      file: undefined,
      entityCreateFields: initialEntityCreateFields,
    },
  });

  const { getValues } = methods;

  const handleCloseDrawer = useEvent((): void => {
    onCloseDrawer();
    if (processStatistics.totalToProcess > 0) {
      // Reload page to invalidate cached dropdown items after a new vehicle type is created
      window.location.reload();
    }
  });

  const handleProcessFile = useEvent(async (): Promise<void> => {
    const file = getValues(GENERIC_UPLOAD_FORM_FIELDS.file);
    const entityCreateFields = getValues(
      GENERIC_UPLOAD_FORM_FIELDS.entityCreateFields,
    );
    setPrepareError(undefined);

    const prepareFileResult = await prepareFile(file, entityCreateFields);

    if (!prepareFileResult.success || !prepareFileResult.data) {
      setPrepareError(simpleResultErrorInfo(prepareFileResult, t));
      return;
    }

    await checkPhoneConflicts(prepareFileResult.data.data);
    setCurrentStep(GenericUploadStepEnum.Preview);
  });

  const handleStartProcessing = useEvent(async (): Promise<void> => {
    setCurrentStep(GenericUploadStepEnum.Process);
    processUploadData(
      excludeSkippedUploadRows(uploadResult?.data || [], skippedDataIndexes),
    );
  });

  const isLoading = isPreparing || isProcessing || isCheckingPhones;
  const isSaveDisabled = useMemo(() => {
    if (currentStep === GenericUploadStepEnum.Preview) {
      const rowsToProcess =
        uploadStatistics.totalRows - skippedDataIndexes.length;

      // Every conflicting row must be explicitly skipped, and something must be left to process.
      if (rowsToProcess <= 0 || unresolvedPhoneConflictRowsCount > 0) {
        return true;
      }
    }

    return isLoading;
  }, [
    currentStep,
    isLoading,
    skippedDataIndexes.length,
    unresolvedPhoneConflictRowsCount,
    uploadStatistics.totalRows,
  ]);

  return (
    <BaseDrawer onCloseDrawer={handleCloseDrawer} drawerNumber={drawerNumber}>
      <CommonDefaultDrawer
        title={t("general.IMPORT_CSV")}
        onCloseDrawer={handleCloseDrawer}
        Footer={
          <GenericUploadFooter
            currentStep={currentStep}
            onUploadClick={handleProcessFile}
            onProcessClick={handleStartProcessing}
            onCancelClick={handleCloseDrawer}
            isLoading={isLoading}
            isSaveDisabled={isSaveDisabled}
          />
        }
      >
        <FormProvider {...methods}>
          {currentStep === GenericUploadStepEnum.Upload && (
            <GenericUploadForm prepareError={prepareError} />
          )}
          {currentStep === GenericUploadStepEnum.Preview && (
            <GenericUploadPreview
              statistics={uploadStatistics}
              uploadResult={uploadResult}
              phoneConflicts={phoneConflicts}
              skippedDataIndexes={skippedDataIndexes}
              unresolvedPhoneConflictRowsCount={
                unresolvedPhoneConflictRowsCount
              }
              isExistingPhoneCheckFailed={isExistingPhoneCheckFailed}
              onToggleSkippedRow={toggleSkippedRow}
            />
          )}
          {currentStep === GenericUploadStepEnum.Process && (
            <GenericUploadProcess
              statistics={processStatistics}
              failedItems={processFailedItems}
              isProcessing={isProcessing}
            />
          )}
        </FormProvider>
      </CommonDefaultDrawer>
    </BaseDrawer>
  );
};
